// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   Util.js
// Author: Percipient Reverence
// Date:   29-Mar-2023
//
// Description:
//
//   Utility functions and global variables.

// MUI imports
import { gridExpandedSortedRowIdsSelector } from '@mui/x-data-grid';
import { createTheme } from '@mui/material/styles';

// Configuration constants

export const ConfigVars = {
  SectionsPerPage:            40,
  ArticlesPerPage:            40,
  MessagesPerPage:            15,
  MillisecondsPerHour:        3600000,
  MillisecondsPerDay:         86400000,
  MillisecondsPerWeek:        604800000,
  OriginYear:                 2023,
  CompanyName:                'Qwixotic',
  DefaultSiteName:            'NewsTalk',
  StrictMode:                 false,
  Avatars_DisplayWidth:       50,
  Avatars_DisplayHeight:      60,
  Avatars_DisplayFontSize:    30,
  Logo:                       'NewsTalkLogo2.png'
}

export const nodeJsURL    = process.env.REACT_APP_NODEJSURL

// The object could be anything: variable, array, object.  I'm striving for a PHP style of
// presentation, don't know if I'll achieve it.
export const objectToText = async (v, depth=0) => {
  if (depth > 3) {
    return;
  }
  const indent = ' '.repeat(depth*2)
  switch (typeof(v)) {
  case 'string':
    return '\'' + v + '\'';
  case 'number':
    return String(v);
  case 'boolean':
    return String(v);                       // Will become either "true" or "false"
  case 'object':
    // The null value is considered an object, so check for it
    if (v === null) {
      return 'null'
    }
    let newIndent = indent + '  '
    let Rows = []
    if (Array.isArray(v)) {
      Rows.push('Array (')
      for (let i=0; i<v.length; i++) {
        let element = v[i]
        let thisValue = await objectToText(element, depth++)
        Rows.push(newIndent + '[' + i + '] => ' + thisValue)
      }
      Rows.push(indent + ')')
    } else {
      Rows.push('Object {')
      for (let thisIndex in v) {
        let thisValue = await objectToText(v[thisIndex], depth++)
        let indexName = typeof(thisIndex) == 'number' ? thisIndex : '\'' + thisIndex + '\''
        Rows.push(newIndent + '[' + indexName + '] => ' + thisValue)
      }
      Rows.push(indent + '}')
    }
    return Rows.join(['\n'])
  case 'function':
    return String(v);                       // Will become the text of the function definition
  default:
    return typeof(v);
  }
}

// The getCookie and setCookie functions are stolen from dBoard

export const getCookie = (name) => {
  const cname = "; " + name + "=";
  const dc = "; " + document.cookie;
  if (dc.length > 0) {
    let begin = dc.indexOf(cname);
    if (begin !== -1) {
      begin += cname.length;
      let end = dc.indexOf(";", begin);
      if (end === -1) end = dc.length;
      return unescape(dc.substring(begin, end));
    }
  }
  return null;
}

// Hardwired to set expires date to a year in the future
function setCookie(name, value, path) {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  setCookieWithTime(name, value, path, date)
}

function deleteCookie(name, path) {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  setCookieWithTime(name, '', path, date)
}

function setCookieWithTime(name, value, path, date) {
  const expires = '; expires=' + date.toGMTString();
  if (path != null) {
    path = '; path=' + path;
  }
  document.cookie = name + '=' + value + expires + path;
}  

export const setSigninCookies = (mbrid, password) => {
  console.log('Writing cookies: mbrid: ' + mbrid + ', password: ' + password)
  setCookie('mbrid', mbrid, '/')
  setCookie('qx', password, '/')
}

export const deleteSigninCookies = () => {
  console.log('Deleting mbrid and password cookies')
  deleteCookie('mbrid', '/')
  deleteCookie('qx', '/')
}

export const gotoComment = (MID, apiRef, initialTimeout=0) => {
  setTimeout(() => {
    const rowIndex = gridExpandedSortedRowIdsSelector(apiRef).findIndex((id) => id ? id === MID : false)
    const pageIndex = Math.floor(rowIndex / ConfigVars.MessagesPerPage)
    setTimeout(() => apiRef.current.setPage(pageIndex)) // Internally the page numbering begins at 0
    setTimeout(() => document.getElementById('m' + MID).scrollIntoView(true), 400)
  }, initialTimeout)

  // What follows is the approach I actually want to use but that doesn't work.  I've set this to
  // just go to the row with an index of 20 (the 21st row).  Since anywhere on the row is fine a
  // column index of 0 is fine.
  //
  // const rowInfo = {rowIndex: 20, colIndex: 0}
  // apiRef.current.scrollToIndexes(rowInfo)
};

export const myFetch = (url, options=null) => {
  if (options) {
    return fetch(nodeJsURL + url, options)
  } else {
    return fetch(nodeJsURL + url)
  }
}

// Same as myFetch except options are built-in for POST style.  User cannot provide his own
// options.  If he needs to do that he should use myFetch.
export const myPost = (url, data) => {
  const options = {
    method: "POST",
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(data)
  }
  return fetch(nodeJsURL + url, options)
}

// Same as myPost except posts an object of type FormData.  The form data is passed as the value of
// the options.body field, but the fields of the FormData object arrive at their destination in two
// different ways depending upon type.  Numbers and strings remain in the body object, but files are
// put into a new object called 'files'.
export const myPostForm = (url, formData) => {
  // We use the FormData object because it automatically does the right thing for uploading files,
  // including image files, which have to be of content type multipart/form-data.  This arrives at
  // the server as an array of information for each file.
  const options = {
    method: "POST",
    body: formData
  }
  return fetch(nodeJsURL + url, options)
}

// User names are restricted to the characters 
export const nameIsLegal = (name) => {
  if (!name || typeof(name) !== 'string') {
    return false;
  }
  // Remove leading/trailing white space - don't inform user, we'll always be trimming it anyway
  name = name.trim()
  // Replace consecutive spaces with a single space
  name = name.replace(/\s{2,}/, ' ')
  // Keep user names to a max of 20 characters, just so we don't even have to see unwieldy names.
  // Don't include '@' so can easily distinguish member names from email addresses
  return /^[\w\d !?/$&^+\-~.:;\]['"{}]{1,20}$/.test(name)
}

// It would be nice if there were a regular expression that covered all the rules for legal email
// addresses, but I can't find one, and I'm positive that I couldn't write one myself because the
// rules are so complicated, and so I have broken down the checking into several steps.  I describe
// the rules as I understand them as I go.  I'm going by the Wikipedia description at:
//
//    https://en.wikipedia.org/wiki/Email_address.
//
export const emailIsLegal = (email) => {
  // An email address is a string of ASCII characters consists of three parts in this order:
  //  1. The local portion, which can consist of:
  //      a. Alphanumeric characters
  //      b. These special characters: !#$%&'*+-/=?^_`{|}~,
  //      c. dot (.), but not as the first or last character, and not consecutively
  //      d. A quoted string using the double quote character ("), but the entire local portion must
  //         be quoted.
  //      e. When quoted, these additional special characters are legal, some requiring that they be
  //         preceded by a backslash: "(),:;<>@[\]
  //      f. Comments placed between parentheses are legal if they lead or follow the local portion
  //      g. It can have a maximum length of 64 characters
  //  2. The at sign (@)
  //  3. The domain, which can consist of:
  //      a. Alphanumeric characters
  //      b. Hyphen (-), but not first or last
  //      c. IP address is enclosed in square brackets
  //
  // Interestingly, Plesk only accepts the simplest of email addresses.  For instance, it doesn't
  // accept email addresses that begin with a special character or a comment.  Therefore I can't use
  // Plesk to test what are legal email addresses.

  // The maximum length of an email address is 254 characters
  if (email.length > 254) {
    return false
  }

  // Split the email address into the local portion and the domain
  const EmailSegments = email.split('@')
  if (EmailSegments.length !== 2) {
    return false;
  }
  const [localPortion, domain] = EmailSegments
  if (localPortion === null || domain === null) {
    return false;
  }

  // Check the local portion.  First check the max length:
  if (localPortion.length > 64) {
    return false
  }
  // Check for leading/trailing dots and presence of illegal characters
  let regex = /^[\d\w!#$%&'*+\-/=?^`{|}~,][\d\w!#$%&'*+\-/=?^`{|}~,.]{0,62}[\d\w!#$%&'*+\-/=?^`{|}~,]$/
  if (!regex.test(localPortion)) {
    return false
  }
  // Check for consecutive dots
  if (/\.\./.test(localPortion)) {
    return false
  }

  // Check the domain portion.  First check the max length.
  if (domain.length > 253) {
    return false
  }
  // Split into subdomains
  const SubDomains = domain.split('.')
  if (SubDomains.length < 2) {
    return false
  }
  for (const subDomain of SubDomains) {
    if (subDomain.length === 0 || subDomain.length > 63) {
      return false
    }
    if (!/^[\w\d-]+$/.test(subDomain)) {
      return false
    }
  }

  // No problems found, return true
  return true
}

export const nameOrEmailIsLegal = (nameOrEmail) => {
  // Members can sign in using either one of their aliases or their email address.  Email addresses
  // are requred to have the '@' character, while names cannot include the '@' character, and that
  // is how we tell the two appart.
  if (nameOrEmail.includes('@')) {
    // It's an email address
    return emailIsLegal(nameOrEmail)
  } else {
    // It's a member alias
    return nameIsLegal(nameOrEmail)
  }
}

export const passwordIsLegal = (password) => {
  if (password.length < 6 || password.length > 32) {
    return false
  }
  if (/[\w\d!@#$%^&*()-=+[{\]};:'",<.>/?]{6,32}/.test(password)) {
    return true
  }
  return false
}

export const standardTheme = createTheme({
  typography: {
    fontFamily: 'Roboto, Arial, Helvetica',
    fontSize: 20
  }})

// There's an example comment for each date format with '/' as separator and 4 the number of year
// digits
export const FormatDate = (theDate, dateFormat, dateSep, yearDig, padDays=false, padMonths=false) => {
  const year = yearDig === 4
        ? theDate.getFullYear()
        : String(theDate.getFullYear()).substr(2)
  const month = theDate.getMonth(); // 0-11
  const monthName = months[month]
  const monthNameLong = longMonths[month]
  const monthPadded = padMonths ? String(month+1).padStart(2, '0') : month+1
  const dayOfMonth = theDate.getDate(); // 1-31
  const dayOfMonthPadded = padDays ? String(dayOfMonth).padStart(2, '0') : dayOfMonth
  const dayOfWeek = theDate.getDay(); // 0-6
  const nameOfDayOfWeek = days[dayOfWeek] + ' ';
  const nameOfDayOfWeekLong = longDays[dayOfWeek] + ' ';
  switch(dateFormat) {
  case 'US':                    // 8/2/2023
    return monthPadded + dateSep + dayOfMonthPadded + dateSep + year;
  case 'USAM':                  // Aug/2/2023
    return monthName + dateSep + dayOfMonthPadded + dateSep + year;
  case 'USFM':                  // August22023
    return monthNameLong + dayOfMonthPadded + year;
  case 'USADOW':                // Sun 8/2/2023
    return nameOfDayOfWeek + monthPadded + dateSep + dayOfMonthPadded + dateSep + year;
  case 'USFDOW':                // Sunday 8/2/2023
    return nameOfDayOfWeekLong + monthPadded + dateSep + dayOfMonthPadded + dateSep + year;
  case 'USAMADOW':              // Sun Aug/2/2023
    return nameOfDayOfWeek + monthName + dateSep + dayOfMonthPadded + dateSep + year;
  case 'USAMFDOW':              // Sunday Aug/2/2023
    return nameOfDayOfWeekLong + monthName + dateSep + dayOfMonthPadded + dateSep + year;
  case 'USFMADOW':              // Sun August 2, 2023
    return nameOfDayOfWeek + monthNameLong + ' ' + dayOfMonthPadded + ', ' + year;
  case 'USFMFDOW':              // Sun August 2, 2023
    return nameOfDayOfWeek + monthNameLong + ' ' + dayOfMonthPadded + ', ' + year;
  case 'E':                     // 2/8/2023
    return dayOfMonthPadded + dateSep + monthPadded + dateSep + year;
  case 'EAM':                   // 2/Aug/2023
    return dayOfMonthPadded + dateSep + monthName + dateSep + year;
  case 'EFM':                   // 2/August/2023
    return dayOfMonthPadded + dateSep + monthNameLong + dateSep + year;
  case 'EADOW':                 // Sun 2/8/2023
    return nameOfDayOfWeek + dayOfMonthPadded + dateSep + monthPadded + dateSep + year;
  case 'EFDOW':                 // Sunday 2/8/2023
    return nameOfDayOfWeekLong + dayOfMonthPadded + dateSep + monthPadded + dateSep + year;
  case 'EAMADOW':               // Sun 2/Aug/2023
    return nameOfDayOfWeek + dayOfMonthPadded + dateSep + monthName + dateSep + year;
  case 'EAMFDOW':               // Sunday 2/Aug/2023
    return nameOfDayOfWeekLong + dayOfMonthPadded + dateSep + monthName + dateSep + year;
  case 'EFMADOW':               // Sun 2/August/2023
    return nameOfDayOfWeek + dayOfMonthPadded + dateSep + monthNameLong + dateSep + year;
  case 'EFMFDOW':               // Sunday 2/August/2023
    return nameOfDayOfWeekLong + dayOfMonthPadded + dateSep + monthNameLong + dateSep + year;
  case 'I':                     // 2023/8/2
    return year + dateSep + monthPadded + dateSep + dayOfMonthPadded;
  case 'IAM':                   // 2023/Aug/2
    return year + dateSep + monthName + dateSep + dayOfMonthPadded;
  case 'IFM':                   // 2023/August/2
    return year + dateSep + monthNameLong + dateSep + dayOfMonthPadded;
  case 'IADOW':                 // Sun 2023/8/2
    return nameOfDayOfWeek + year + dateSep + monthPadded + dateSep + dayOfMonthPadded;
  case 'IFDOW':                 // Sunday 2023/8/2
    return nameOfDayOfWeekLong + year + dateSep + monthPadded + dateSep + dayOfMonthPadded;
  case 'IAMADOW':               // Sun 2023/Aug/2
    return nameOfDayOfWeek + year + dateSep + monthName + dateSep + dayOfMonthPadded;
  case 'IAMFDOW':               // Sunday 2023/Aug/2
    return nameOfDayOfWeekLong + year + dateSep + monthName + dateSep + dayOfMonthPadded;
  case 'IFMADOW':               // Sun 2023/August/2
    return nameOfDayOfWeek + year + dateSep + monthNameLong + dateSep + dayOfMonthPadded;
  case 'IFMFDOW':               // Sunday 2023/August/2
    return nameOfDayOfWeekLong + year + dateSep + monthNameLong + dateSep + dayOfMonthPadded;
  default:
    return '*** Bad Date Format Requested: ' + dateFormat + ' ***';
  }
}

export const padZeros = (val, num=2) => {
  return String(val).padStart(num, '0')
}
  
const days       = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const longDays   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
const months     = ['Jan','Feb','Mar','Apr','May','Jun', 'Jul','Aug','Sep','Oct','Nov','Dec']
const longMonths = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December']
